import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { fetchWithTimeout } from "../../utils/fetchWithTimeout";
import { wrapFetchWithSignal } from "../../utils/abort";
import { makeFetchWithAuth } from "../../auth/authFetch";

import {
  AdminAccessClient,
  AdminUsersClient,
  CreateUserRequest,
  ResetUserPasswordRequest,
  UpdateUserAccessRequest,
  UpdateUserClinicsRequest,
  UpdateUserRequest,
} from "../../web-api-client";
import { appConfig } from "../../appconfig";

export function useAccessDefinition() {
  const navigate = useNavigate();

  return useQuery({
    queryKey: ["admin-access-definition"],
    queryFn: async ({ signal }) => {
      const client = new AdminAccessClient(appConfig.apiBaseUrl, {
        fetch: makeFetchWithAuth(fetchWithTimeout(), {
          onUnauthorized: () =>
            navigate("/login", {
              replace: true,
              state: { from: window.location.pathname },
            }),
        }),
      });

      return (await (client as any).getApiAdminAccessDefinition()) as any;
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useAdminUsers(keyword: string, page = 1, size = 50, departmentId?: number | null) {
  const navigate = useNavigate();

  return useQuery({
    queryKey: ["admin-users", { keyword, page, size, departmentId }],
    queryFn: async ({ signal }) => {
      const client = new AdminUsersClient(appConfig.apiBaseUrl, {
        fetch: makeFetchWithAuth(fetchWithTimeout(), {
          onUnauthorized: () =>
            navigate("/login", {
              replace: true,
              state: { from: window.location.pathname },
            }),
        }),
      });

      return client.getApiAdminUsers(
        keyword?.trim() ? keyword.trim() : undefined,
        departmentId ?? undefined,
        page,
        size
      );
    },
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useUserAccess(userId?: string | null) {
  const navigate = useNavigate();

  return useQuery({
    queryKey: ["admin-user-access", userId],
    enabled: !!userId,
    queryFn: async ({ signal }) => {
      const client = new AdminAccessClient(appConfig.apiBaseUrl, {
        fetch: makeFetchWithAuth(fetchWithTimeout(), {
          onUnauthorized: () =>
            navigate("/login", {
              replace: true,
              state: { from: window.location.pathname },
            }),
        }),
      });

      return client.getApiAdminAccessUsersAccess(userId!);
    },
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
}

export function useUpdateUserAccess() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (args: {
      userId: string;
      roles: string[];
      grants: string[];
      denies: string[];
    }) => {
      const client = new AdminAccessClient(appConfig.apiBaseUrl, {
        fetch: makeFetchWithAuth(fetchWithTimeout(), {
          onUnauthorized: () =>
            navigate("/login", {
              replace: true,
              state: { from: window.location.pathname },
            }),
        }),
      });

      const req = new UpdateUserAccessRequest() as any;
      req.roles = args.roles;
      req.grants = args.grants;
      req.denies = args.denies;

      await client.putApiAdminAccessUsersAccess(args.userId, req);
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      qc.invalidateQueries({ queryKey: ["admin-user-access", vars.userId] });
    },
  });
}

export function useCreateUser() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (args: {
      name: string;
      email: string;
      departmentId?: number | null;
      role: string;
      password: string;
      clinicIds?: number[];
    }) => {
      const client = new AdminUsersClient(appConfig.apiBaseUrl, {
        fetch: makeFetchWithAuth(fetchWithTimeout(), {
          onUnauthorized: () =>
            navigate("/login", {
              replace: true,
              state: { from: window.location.pathname },
            }),
        }),
      });

      const req = new CreateUserRequest() as any;
      req.name = args.name;
      req.email = args.email;
      req.departmentId = args.departmentId ?? undefined;
      req.role = args.role;
      req.password = args.password;
      req.clinicIds = (args.clinicIds ?? []).length > 0 ? args.clinicIds : undefined;

      return client.postApiAdminUsers(req);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    },
  });
}

export function useUpdateUserInfo() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (args: {
      userId: string;
      name: string;
      email: string;
      departmentId?: number | null;
    }) => {
      const client = new AdminUsersClient(appConfig.apiBaseUrl, {
        fetch: makeFetchWithAuth(fetchWithTimeout(), {
          onUnauthorized: () =>
            navigate("/login", {
              replace: true,
              state: { from: window.location.pathname },
            }),
        }),
      });

      const req = new UpdateUserRequest() as any;
      req.name = args.name;
      req.email = args.email;
      req.departmentId = args.departmentId ?? undefined;

      return client.putApiAdminUsers(args.userId, req);
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      qc.invalidateQueries({ queryKey: ["admin-user-access", vars.userId] });
    },
  });
}

export function useUpdateUserClinics() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (args: { userId: string; clinicIds: number[] }) => {
      const client = new AdminUsersClient(appConfig.apiBaseUrl, {
        fetch: makeFetchWithAuth(fetchWithTimeout(), {
          onUnauthorized: () =>
            navigate("/login", {
              replace: true,
              state: { from: window.location.pathname },
            }),
        }),
      });

      const req = new UpdateUserClinicsRequest();
      req.clinicIds = args.clinicIds ?? [];

      return client.putApiAdminUsersClinics(args.userId, req);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      // qc.invalidateQueries({ queryKey: ["admin-user-clinics"] });
    },
  });
}

export function useDeleteUser() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      const client = new AdminUsersClient(appConfig.apiBaseUrl, {
        fetch: makeFetchWithAuth(fetchWithTimeout(), {
          onUnauthorized: () =>
            navigate("/login", {
              replace: true,
              state: { from: window.location.pathname },
            }),
        }),
      });

      await client.deleteApiAdminUsers(userId);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    },
  });
}

export function useResetUserPassword() {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async (args: { userId: string; newPassword: string }) => {
      const client = new AdminUsersClient(appConfig.apiBaseUrl, {
        fetch: makeFetchWithAuth(fetchWithTimeout(), {
          onUnauthorized: () =>
            navigate("/login", {
              replace: true,
              state: { from: window.location.pathname },
            }),
        }),
      });

      const req = new ResetUserPasswordRequest();
      req.newPassword = args.newPassword;

      await client.putApiAdminUsersPassword(args.userId, req);
    },
  });
}
